import { PricePresentation } from "@/types/Products";
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function calculateProfitUplift(optimizationData: any): number {
    if (
        !optimizationData?.plot_data?.x_values ||
        !optimizationData?.plot_data?.y_values ||
        !optimizationData?.original_price ||
        !optimizationData?.best_prices?.length
    ) {
        return 0;
    }

    const xValues = optimizationData.plot_data.x_values;
    const yValues = optimizationData.plot_data.y_values;

    // Find index closest to original price
    const originalIndex = xValues.reduce(
        (closest: number, value: number, index: number) => {
            return Math.abs(value - optimizationData.original_price) <
                Math.abs(xValues[closest] - optimizationData.original_price)
                ? index
                : closest;
        },
        0
    );

    // Find index closest to best price (last one in the array)
    const bestPrice =
        optimizationData.best_prices[optimizationData.best_prices.length - 1];
    const bestIndex = xValues.reduce(
        (closest: number, value: number, index: number) => {
            return Math.abs(value - bestPrice) <
                Math.abs(xValues[closest] - bestPrice)
                ? index
                : closest;
        },
        0
    );

    const originalPpv = yValues[originalIndex];
    const bestPpv = yValues[bestIndex];

    // Calculate the difference
    const uplift = bestPpv / originalPpv - 1;

    // Handle NaN values by returning 0
    return isNaN(uplift) ? 0 : uplift;
}

export function applyPricePresentation(price: number, presentation: PricePresentation): number {
    switch (presentation) {
        case "whole":
            return Math.round(price);
        case "charm_99":
            return Math.round(price) - 0.01;
        case "charm_95":
            return Math.round(price) - 0.05;
        case "charm_49_99":
            return Math.round(price * 2) / 2 - 0.01;
        case "subunit_5":
            return Math.round(price / 0.05) * 0.05;
        case "subunit_10":
            return Math.round(price / 0.1) * 0.1;
        case "subunit_25":
            return Math.round(price / 0.25) * 0.25;
        case "subunit_50":
            return Math.round(price / 0.5) * 0.5;
        case "whole_5":
            return Math.round(price / 5) * 5;
        case "whole_10":
            return Math.round(price / 10) * 10;
        case "whole_25":
            return Math.round(price / 25) * 25;
        case "whole_50":
            return Math.round(price / 50) * 50;
        case "whole_100":
            return Math.round(price / 100) * 100;
        default:
            return price;
    }
}